import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';

const RBACContext = createContext();

export function useRBAC() {
  return useContext(RBACContext);
}

// Available Modules
export const MODULES = {
  DASHBOARD: 'dashboard',
  USERS: 'users',
  ROLES: 'roles',
  PROJECTS: 'projects',
  STORE: 'store',
  WORKING: 'working',
  BOQ: 'boq',
  BATCHING_PLANT: 'batching_plant',
  ANALYSIS_DASHBOARD: 'analysis_dashboard',
  DPR: 'dpr',
  PLANNING: 'planning',
  LABOUR: 'labour',
  EQUIPMENT: 'equipment',
  VEHICLE: 'vehicle',
  FUEL: 'fuel',
  FINANCE: 'finance',
  TEST_REPORTS: 'test_reports',
  IMPORTANT_DOCUMENTS: 'important_documents',
  SUBCONTRACTOR_RATES: 'subcontractor_rates',
  CLIENT_BILLING: 'client_billing',
  VILLA_SALES: 'villa_sales',
  DRAWINGS: 'drawings',
  PROGRESS_TRACKING: 'progress_tracking',
  SALARY: 'salary',
  BG_MANAGEMENT: 'bg_management',
  TENDER_MANAGEMENT: 'tender_management',
  ITEM_KNOWLEDGE_BASE: 'item_knowledge_base',
  PROCUREMENT_VENDOR: 'procurement_vendor',
  AI_INTEGRATION: 'ai_integration',
  YARD: 'yard',
  ACCOUNTANT_WORKBOOK: 'accountant_workbook',
  ATTENDANCE: 'attendance',
  WEIGHBRIDGE: 'weighbridge',
  DIGITAL_VIEWER: 'digital_viewer',
  CONSTRUCTION_ACTIVITY_TRACKER: 'construction_activity_tracker',
  STORE_DOCUMENT_EXPLORER: 'store_document_explorer',
  WHATSAPP_INTELLIGENCE: 'whatsapp_intelligence',
  RECRUITMENT: 'recruitment',
  PO_MANAGEMENT: 'po_management',
  TENDER_INTELLIGENCE: 'tender_intelligence'
};

export const ALL_MODULES = Object.values(MODULES);

// Access Levels
export const ACCESS_LEVELS = {
  NONE: 'none',
  READ: 'read',
  WRITE: 'write'
};

export function RBACProvider({ children }) {
  const { currentUser } = useAuth();
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setRoles({});
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(collection(db, "roles"), (snapshot) => {
      const fetchedRoles = {};
      const rolesToMigrate = [];
      snapshot.forEach(docSnap => {
        const roleData = docSnap.data();
        fetchedRoles[docSnap.id] = roleData;
        if (
          !roleData.permissions ||
          roleData.permissions[MODULES.PO_MANAGEMENT] === undefined ||
          roleData.permissions[MODULES.TENDER_INTELLIGENCE] === undefined
        ) {
          rolesToMigrate.push({ id: docSnap.id, data: roleData });
        }
      });
      setRoles(fetchedRoles);
      setLoading(false);

      // Auto-backfill PO Management & Tender Intelligence to existing roles in Firestore if logged in as Admin
      if (['Admin', 'Super Admin'].includes(currentUser?.role) && rolesToMigrate.length > 0) {
        rolesToMigrate.forEach(async ({ id, data }) => {
          try {
            const isManagerRole = ['Store In-Charge', 'Store Manager', 'Project Manager', 'Procurement Manager', 'Accountant'].includes(id);
            const defaultPO = {
              view: true,
              create: true,
              edit: true,
              delete: isManagerRole
            };
            await setDoc(doc(db, "roles", id), {
              ...data,
              permissions: {
                ...(data.permissions || {}),
                [MODULES.PO_MANAGEMENT]: data.permissions?.[MODULES.PO_MANAGEMENT] || defaultPO,
                [MODULES.TENDER_INTELLIGENCE]: data.permissions?.[MODULES.TENDER_INTELLIGENCE] || defaultPO
              }
            }, { merge: true });
          } catch (err) {
            console.warn(`[RBAC] Failed to auto-migrate role ${id}:`, err.message);
          }
        });
      }
    }, (error) => {
      console.error("Error fetching roles:", error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);
  
  const getPermission = (module) => {
    if (!currentUser || !currentUser.role) return { view: false, create: false, edit: false, delete: false };
    if (['Admin', 'Super Admin'].includes(currentUser.role)) return { view: true, create: true, edit: true, delete: true };
    
    const userRole = roles[currentUser.role];
    if (!userRole || !userRole.permissions) {
      if (module === MODULES.PO_MANAGEMENT || module === MODULES.TENDER_INTELLIGENCE) {
        return { view: true, create: true, edit: true, delete: false, export: true };
      }
      return { view: false, create: false, edit: false, delete: false };
    }
    
    const perm = userRole.permissions[module];
    if (!perm) {
      // PO Management & Tender Intelligence: Default to enabled for all active roles if not explicitly configured in Firestore
      if (module === MODULES.PO_MANAGEMENT || module === MODULES.TENDER_INTELLIGENCE) {
        const isManagerRole = ['Store In-Charge', 'Store Manager', 'Project Manager', 'Procurement Manager', 'Accountant'].includes(currentUser.role);
        return { view: true, create: true, edit: true, delete: isManagerRole, export: true };
      }
      return { view: false, create: false, edit: false, delete: false };
    }
    
    // Legacy string support
    if (perm === 'read' || perm === ACCESS_LEVELS.READ) return { view: true, create: false, edit: false, delete: false };
    if (perm === 'write' || perm === ACCESS_LEVELS.WRITE) return { view: true, create: true, edit: true, delete: true };
    if (perm === 'none' || perm === ACCESS_LEVELS.NONE) return { view: false, create: false, edit: false, delete: false };

    return {
      view: !!perm.view,
      create: !!perm.create,
      edit: !!perm.edit,
      delete: !!perm.delete,
      export: !!perm.export
    };
  };

  const hasAccess = (module, action = 'view') => {
    // Backwards compatibility for old code calling hasAccess(module, ACCESS_LEVELS.WRITE)
    let checkAction = action;
    if (action === 'read' || action === ACCESS_LEVELS.READ) checkAction = 'view';
    if (action === 'write' || action === ACCESS_LEVELS.WRITE) checkAction = 'create'; // Maps general 'write' to 'create' in old modules for hiding new buttons
    
    const perm = getPermission(module);
    return perm[checkAction] === true;
  };

  const updateRole = async (roleName, permissions, description) => {
    try {
      await setDoc(doc(db, "roles", roleName), {
        description,
        permissions
      });
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const deleteRoleFromDb = async (roleName) => {
    try {
      await deleteDoc(doc(db, "roles", roleName));
    } catch (error) {
      console.error("Error deleting role:", error);
    }
  };

  const value = {
    roles,
    getPermission,
    hasAccess,
    updateRole,
    deleteRole: deleteRoleFromDb,
    MODULES,
    ACCESS_LEVELS
  };

  return (
    <RBACContext.Provider value={value}>
      {!loading && children}
    </RBACContext.Provider>
  );
}
